import { Button, List, Modal, Popover, Select } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { Sketch } from '../../../models/sketch.model';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { useMutation } from '@tanstack/react-query';
import './SketchOptions.scss';

interface SketchOptionsButtonProps {
  item: Sketch;
  onZoomToSketch: () => void;
  onSketchProperties: () => void;
  onDelete: () => void;
}

const { Option } = Select;

const SketchOptions: React.FC<SketchOptionsButtonProps> = ({ item, onZoomToSketch, onSketchProperties, onDelete }) => {
  const [visible, setVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportFormat, setExportFormat] = useState<string>('');
  const { deleteMapSketch, saveSketch } = MapPortalService();
  const { mutateAsync: deleteSketchAsync } = useMutation(deleteMapSketch());
  const { mutateAsync: saveSketchAsync } = useMutation(saveSketch());

  const options = [
    {
      label: 'View Sketch Properties',
      disabled: false,
    },
    {
      label: 'Zoom to Sketch',
      disabled: false,
    },
    {
      label: 'Save Sketch As',
      disabled: false,
    },
    {
      label: 'Delete Sketch',
      disabled: false,
    },
  ];

  const handleExport = async () => {
    const data = await saveSketchAsync({
      sketchId: String(item.id!),
      format: exportFormat,
    });

    if (!data) return;

    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${item.title}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    setExportModalVisible(false);
  };

  const handleOptionClick = (option: string) => {
    if (option === 'View Sketch Properties') {
      onSketchProperties();
    } else if (option === 'Zoom to Sketch') {
      if (item.visible) onZoomToSketch();
    } else if (option === 'Save Sketch As') {
      setExportModalVisible(true);
    } else if (option === 'Delete Sketch') {
      deleteSketchAsync({ sketchId: String(item.id!) }).then((res) => {
        if (res) {
          console.log('Sketch deleted successfully');
          onDelete();
        }
      });
    }
    setVisible(false);
  };

  const content = (
    <List
      size="small"
      dataSource={options}
      className="sketch-options__list"
      renderItem={(item) => (
        <List.Item
          onClick={() => !item.disabled && handleOptionClick(item.label)}
          className={['sketch-options__list-item', item.disabled ? 'sketch-options__list-item--disabled' : ''].filter(Boolean).join(' ')}
        >
          {item.label}
        </List.Item>
      )}
    />
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={visible}
        onOpenChange={setVisible}
        placement="bottomRight"
        overlayClassName="sketch-options__popover"
      >
        <Button
          type="text"
          className="sketch-options__trigger"
          aria-label="Sketch options"
          icon={<MoreOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setVisible(!visible);
          }}
        />
      </Popover>
      <Modal title="Save Sketch As" open={exportModalVisible} onCancel={() => setExportModalVisible(false)} onOk={handleExport} okText="Download">
        <div className="sketch-options__export-row">
          <span className="sketch-options__export-label">FILE TYPE</span>
          <Select
            className="sketch-options__export-select"
            placeholder="Select One"
            defaultValue={'shapefile'}
            value={exportFormat}
            onChange={(value) => setExportFormat(value)}
          >
            <Option value="shapefile">shapefile</Option>
            <Option value="kml">kml</Option>
          </Select>
        </div>
      </Modal>
    </>
  );
};

export default SketchOptions;
